import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import {
  pagesWithoutHeaderSelector,
  shouldDisplayPrismicButtonSelector,
  shouldDisplayReportButtonSelector,
  shouldDisplayTranslatorSelector,
  showPoTermsSelector,
} from 'redux/selectors/config';
import applicationActions from 'redux/actions/applicationActions';
import { toPng } from 'html-to-image';
import {
  resetPictures,
  screenshotTaken,
  takeScreenshot,
  uploadPicturesToCMX,
} from 'redux/actions/pictureActions';
import {
  isTakingScreenshotSelector,
  uploadedPicturesSelector,
  uploadingPicturesSelector,
} from 'redux/selectors/picture';
import modalActions from 'redux/actions/modalActions';
import { ReportABug } from 'containers/Modals';
import { useTranslateByProject } from 'hooks/translation-hooks';
import { push } from 'connected-react-router';
import routes from 'Routes/routesConfig';

const { showPrismicChecker, togglePoTerms } = applicationActions;
const REQUESTER = 'bugReporting';

const screenshot = 'screenshot.png';
export const useToolsBar = ({ ref }) => {
  const t = useTranslateByProject();
  const dispatch = useDispatch();
  const pagesWithoutToolsBar = useSelector(pagesWithoutHeaderSelector);
  const showPoTerms = useSelector(showPoTermsSelector);
  const shouldDisplayTranslator = useSelector(shouldDisplayTranslatorSelector);
  const isTakingScreenshot = useSelector(isTakingScreenshotSelector);
  const screenshotUploaded = useSelector(uploadedPicturesSelector(REQUESTER));
  const uploadingPictures = useSelector(uploadingPicturesSelector);
  const shouldDisplayPrismicButton = useSelector(
    shouldDisplayPrismicButtonSelector,
  );
  const shouldDisplayReportButton = useSelector(
    shouldDisplayReportButtonSelector,
  );
  const shouldDisplayToolsBar = !useRouteMatch({
    path: pagesWithoutToolsBar,
    exact: true,
  });
  const [shouldDisplayThisPage, setShouldDisplayThisPage] = useState(
    shouldDisplayToolsBar,
  );
  const [screenshotError, setScreenshotError] = useState(false);
  const dataURLtoFile = (dataurl, filename) => {
    var arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  useEffect(() => {
    if (isTakingScreenshot) {
      setScreenshotError(false);
      dispatch(resetPictures({ requester: REQUESTER }));
      toPng(ref.current, { cacheBust: true })
        .then(currentFile => {
          const picture = {
            currentFile: dataURLtoFile(currentFile, screenshot),
            name: screenshot,
            type: 'image/png',
            uploadedAt: new Date(),
          };
          const successMessage = t('screenshot-taken');
          const errorMessage = t('screenshot-failed');
          dispatch(
            uploadPicturesToCMX({
              pictures: [picture],
              requester: REQUESTER,
              successMessage,
              errorMessage,
              refresher: () =>
                dispatch(
                  uploadPicturesToCMX({
                    pictures: [picture],
                    successMessage,
                    errorMessage,
                    refresh: true,
                    requester: REQUESTER,
                  }),
                ),
            }),
          );
        })
        .catch(err => {
          console.log(err);
          setScreenshotError(true);
        })
        .finally(() => {
          dispatch(screenshotTaken());
        });
    }
  }, [isTakingScreenshot]);

  useEffect(() => {
    if (
      screenshotUploaded.length ||
      screenshotError ||
      uploadingPictures.filter(({ name }) => name === screenshot).length
    ) {
      dispatch(
        modalActions.showModalV2({
          content: ReportABug,
          showCloseIcon: false,
        }),
      );
    }
  }, [uploadingPictures, screenshotError, screenshotUploaded]);

  const handleChange = () => {
    dispatch(togglePoTerms());
  };

  const close = () => {
    setShouldDisplayThisPage(false);
    if (showPoTerms) {
      dispatch(togglePoTerms());
    }
  };

  const report = useCallback(() => {
    if (ref.current === null) {
      return;
    }
    dispatch(takeScreenshot());
  }, [ref]);

  const checkCurrentPrismicMigration = async () => {
    dispatch(showPrismicChecker());
    dispatch(push(routes.PRISMIC_CHECKER));
  };

  return {
    checkCurrentPrismicMigration,
    close,
    handleChange,
    report,
    showPoTerms,
    shouldDisplayTranslator,
    shouldDisplayPrismicButton,
    shouldDisplayReportButton,
    shouldDisplayThisPage,
    t,
  };
};
